import { useCallback, useEffect, useState } from 'react';
import { pricingService, currentSalonId } from '../../_services/pricing.service';
import { PRICING_V2_VISIBLE } from '../../config/featureFlags';

/**
 * One copy of /billing/usage for the whole app: Plan & usage, the alert bar,
 * Bailey's question counter and the receptionist screen all read it, so one
 * fetch serves them and a change made on one (an upgrade, a pack) shows on
 * the others straight away. Kept per location: switching location refetches.
 */
const TTL = 60 * 1000;
let state = { salonId: '', usage: null, error: null, loading: false, at: 0 };
let inflight = null;
const listeners = new Set();

const emit = () => listeners.forEach((fn) => fn(state));
const set = (patch) => { state = { ...state, ...patch }; emit(); };

export function getUsageState() { return state; }

export function loadUsage(force, opts = {}) {
    if (!PRICING_V2_VISIBLE) return Promise.resolve(null);
    let signedIn = false;
    try { signedIn = !!localStorage.getItem('accessToken'); } catch (e) { signedIn = false; }
    if (!signedIn) return Promise.resolve(null);
    const salonId = currentSalonId();
    const fresh = state.salonId === salonId && state.usage && Date.now() - state.at < TTL;
    if (!force && fresh) return Promise.resolve(state.usage);
    if (inflight && inflight.salonId === salonId && !opts.fresh) return inflight.p;
    if (state.salonId !== salonId) set({ salonId, usage: null, error: null });
    set({ loading: true });
    const p = pricingService.usage(salonId, opts.fresh ? { fresh: true } : undefined)
        .then((usage) => {
            if (currentSalonId() === salonId) set({ usage: usage || null, error: null, loading: false, at: Date.now() });
            inflight = null;
            return usage;
        })
        .catch((error) => {
            if (currentSalonId() === salonId) set({ error, loading: false, at: Date.now() });
            inflight = null;
            return null;
        });
    inflight = { salonId, p };
    return p;
}

/** Put a fresh usage (what a POST returned) in place for everyone. */
export function setUsage(usage) {
    if (usage) set({ usage, error: null, at: Date.now(), salonId: currentSalonId() });
}

/** Bailey answered: count it here, so the counter moves without a refetch. */
export function bumpBaileyUsed(by = 1) {
    const u = state.usage;
    if (!u || !u.bailey || u.bailey.allowance == null) return;
    setUsage({ ...u, bailey: { ...u.bailey, used: (Number(u.bailey.used) || 0) + by } });
}

/** The agent refused for quota: the server's count is the truth now. */
export function markBaileyOut(q) {
    const u = state.usage;
    if (!u || (q && q.code === 'bailey_staff_access')) return;
    const allowance = q && q.allowance != null ? q.allowance : (u.bailey && u.bailey.allowance);
    setUsage({ ...u, bailey: { ...(u.bailey || {}), used: q && q.used != null ? q.used : allowance, allowance, resetsAt: (q && q.resetsAt) || (u.bailey && u.bailey.resetsAt) } });
}

/** Test hook. */
export function resetUsageStore() { state = { salonId: '', usage: null, error: null, loading: false, at: 0 }; inflight = null; emit(); }

/**
 * { usage, error, loading, refresh, setUsage }. `enabled: false` reads
 * without fetching (for surfaces that only show what is already known).
 */
export default function usePricingUsage({ enabled = true } = {}) {
    const [snap, setSnap] = useState(state);
    useEffect(() => {
        listeners.add(setSnap);
        setSnap(state);
        return () => { listeners.delete(setSnap); };
    }, []);
    useEffect(() => {
        if (!enabled || !PRICING_V2_VISIBLE) return undefined;
        loadUsage(false);
        // A location switch writes salonId; refetch for the new one.
        const onStorage = (e) => { if (!e || e.key === 'salonId' || e.key === null) loadUsage(false); };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, [enabled]);
    const refresh = useCallback(() => loadUsage(true), []);
    const sameSalon = snap.salonId === currentSalonId();
    return {
        usage: sameSalon ? snap.usage : null,
        error: sameSalon ? snap.error : null,
        loading: snap.loading || (enabled && PRICING_V2_VISIBLE && !snap.usage && !snap.error),
        refresh,
        setUsage,
    };
}
